/**
 * Every key event reaches each scene exactly once.
 *
 * Phaser 3.90's KeyboardManager queues each DOM key event and immediately
 * emits MANAGER_PROCESS, on which every scene's KeyboardPlugin replays the
 * *whole* queue - and the queue is only emptied at the end of the frame. So
 * when several key events land inside one frame, the earlier ones are
 * dispatched again with each new arrival. Its own duplicate guard compares
 * against the previous event only, which a replay from the start never trips.
 *
 * In play that showed up as "sea" typed as "sseaa" (TypingInput has had its
 * own guard since); in the menus five quick arrow presses became 29 moves.
 * Fixing it here covers every scene, including ones not written yet.
 */
import Phaser from 'phaser';

interface QueueOwner {
  queue: KeyboardEvent[];
}

interface PatchedPlugin {
  manager: QueueOwner;
}

let installed = false;

export function installKeyboardOnce(): void {
  if (installed) return;
  installed = true;

  const proto = Phaser.Input.Keyboard.KeyboardPlugin.prototype as unknown as {
    update: (this: PatchedPlugin) => void;
  };
  const original = proto.update;
  // Per plugin, because each active scene must still see each event once.
  const delivered = new WeakMap<PatchedPlugin, WeakSet<KeyboardEvent>>();

  proto.update = function (this: PatchedPlugin): void {
    const manager = this.manager;
    const queue = manager.queue;
    let seen = delivered.get(this);
    if (seen === undefined) {
      seen = new WeakSet();
      delivered.set(this, seen);
    }
    const fresh = queue.filter((event) => !seen.has(event));
    if (fresh.length === 0) return;
    for (const event of fresh) seen.add(event);

    // Phaser's update reads manager.queue; show it only the new events.
    manager.queue = fresh;
    try {
      original.call(this);
    } finally {
      // A handler may have queued more (a synthetic event); keep those too.
      manager.queue = queue.concat(manager.queue.slice(fresh.length));
    }
  };
}
